import React, { useEffect, useState } from "react";

import { ReactComponent as SearchIcon } from "assets/images/search.svg";
import { ReactComponent as CancelIcon } from "assets/images/cancel.svg";

interface SearchBarProps {
  onSearch: (searchTerm: string) => void;
  value?: string;
}

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const SearchBar = ({ onSearch, value }: SearchBarProps) => {
  const [inputText, setInputText] = useState<string>("");
  const [isInputText, setIsInputText] = useState<boolean>(false);

  const debouncedInputText = useDebounce(inputText, 200);

  useEffect(() => {
    setIsInputText(debouncedInputText.length > 0);
  }, [debouncedInputText]);

  useEffect(() => {
    if (debouncedInputText) {
      onSearch(inputText);
    } else {
      onSearch("");
    }
  }, [debouncedInputText, onSearch]);

  useEffect(() => {
    if (value) {
      setInputText(value);
      onSearch(value);
    }
  }, [value]);

  const handleInputText = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
  };

  const handleCancelClick = () => {
    setInputText("");
    onSearch("");
  };

  return (
    <div className="flex items-center h-[52px] px-5 bg-white rounded-2xl shadow-card mb-4 mobile:rounded-none mobile:mb-3">
      <SearchIcon className="w-5 h-5 flex-shrink-0" style={{ stroke: '#9CA3AF' }} />
      <input
        className="flex-1 h-full px-3 text-sm font-medium text-achromatic-900 placeholder-achromatic-300 bg-transparent focus:outline-none"
        type="text"
        placeholder="호스트나 서비스를 검색해 보세요"
        value={inputText}
        onChange={handleInputText}
      />
      {isInputText && (
        <div className="cursor-pointer flex-shrink-0 p-1 rounded-full hover:bg-achromatic-100 transition-colors" onClick={handleCancelClick}>
          <CancelIcon className="w-4 h-4" style={{ stroke: '#9CA3AF' }} />
        </div>
      )}
    </div>
  );
};

export default SearchBar;
